import React, { useState, useEffect } from 'react';
import { getAuditLogs } from '../../../services/audit';
import DataTable from '../../../components/Tables/DataTable';
import { 
  ShieldCheck, 
  History, 
  User, 
  Database, 
  Clock, 
  Search,
  Activity,
  ArrowRight
} from 'lucide-react';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await getAuditLogs();
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Security Audit Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getActionStyle = (action) => {
    const act = action?.toLowerCase() || '';
    if (act.includes('create') || act.includes('add')) return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (act.includes('delete') || act.includes('remove')) return 'bg-rose-50 text-rose-700 border-rose-100';
    if (act.includes('update') || act.includes('edit')) return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-slate-50 text-slate-600 border-slate-100';
  };

  const filteredLogs = logs.filter(log => 
    log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.User?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.table_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { 
      key: 'created_at',  // ✅ fixed: use underscore to match backend
      label: 'Date Created',
      render: (val) => {
        if (!val) return '-';
        const date = new Date(val);
        if (isNaN(date.getTime())) return '-';
        // Show both date and time
        return date.toLocaleString();
      }
    },
    { 
      key: 'User.full_name', 
      label: 'Performed By',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100">
            <User size={14} />
          </div>
          <span className="font-bold text-slate-700">
            {row?.User?.full_name || row?.user?.full_name || 'System'}
          </span>
        </div>
      )
    },
    { 
      key: 'action', 
      label: 'Action Taken',
      render: (val) => (
        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${getActionStyle(val)}`}>
          {val?.replace('_', ' ')}
        </span>
      )
    },
    { 
      key: 'table_name', 
      label: 'Resource',
      render: (val) => (
        <div className="flex items-center gap-1.5 text-slate-500 font-medium capitalize">
          <Database size={14} className="text-slate-300" />
          {val?.replace('_', ' ')}
        </div>
      )
    },
    { 
      key: 'record_id', 
      label: 'Target ID',
      render: (val) => (
        <span className="font-mono text-[11px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
          #{val}
        </span>
      )
    },
  ];

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2 text-rose-600">
            <ShieldCheck size={20} />
            <span className="text-[10px] font-black uppercase tracking-[0.3em]">Security Compliance</span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Audit Logs</h1>
          <p className="text-slate-500 font-medium mt-1">
            Immutable trail of all administrative actions and system modifications.
          </p>
        </div>

        <div className="flex items-center gap-4 bg-white p-2 rounded-2xl border border-slate-100 shadow-sm">
            <div className="px-4 text-center border-r border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase">Total Events</p>
                <p className="text-lg font-black text-slate-900">{logs.length}</p>
            </div>
            <div className="px-4 text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase">Retention</p>
                <p className="text-lg font-black text-slate-900">90 Days</p>
            </div>
        </div>
      </div>

      {/* SEARCH BAR */}
      <div className="max-w-md relative group">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
        <input 
          type="text" 
          placeholder="Filter by user, action, or resource..."
          className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 transition-all text-sm shadow-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* DATA TABLE */}
      <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-xl shadow-slate-100/50 overflow-hidden min-h-[400px]">
        <div className="px-8 py-5 border-b border-slate-50 bg-slate-50/30 flex items-center justify-between">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <History size={16} className="text-indigo-600" />
                System Activity Ledger
            </h3>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Showing {filteredLogs.length} Entries</span>
        </div>
        <DataTable columns={columns} data={filteredLogs} loading={loading} />
      </div>

      {/* FOOTER HINT */}
      <div className="flex items-center justify-center gap-2 text-slate-400 text-xs font-medium italic">
        <Activity size={14} />
        Logs are synchronized in real-time with the secure vault.
      </div>

    </div>
  );
};

export default AuditLogs;